// Base API configuration and utilities
import { ENV_CONFIG } from '../config/environment';

export const API_CONFIG = {
  BACKEND_URL: ENV_CONFIG.API.BACKEND_URL,
  MOCK_URL: ENV_CONFIG.API.MOCK_URL,
  TIMEOUT: ENV_CONFIG.API.TIMEOUT
};

// Common headers for API requests
const getCommonHeaders = (token, additionalHeaders = {}) => ({
  'Content-Type': 'application/json',
  'Accept': 'application/json',
  ...(token && { Authorization: `Bearer ${token}` }),
  ...additionalHeaders
});

// Generic API request handler with error handling
export const apiRequest = async (url, options = {}) => {
  const { timeout = API_CONFIG.TIMEOUT, signal: externalSignal, ...restOptions } = options;
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  // If an external signal is provided, abort when it's aborted
  if (externalSignal) {
    externalSignal.addEventListener('abort', () => {
      controller.abort();
    });
  }
  
  try {
    const response = await fetch(url, {
      ...restOptions,
      signal: controller.signal,
      headers: getCommonHeaders(options.token, restOptions.headers)
    });
    
    clearTimeout(timeoutId);
      if (!response.ok) {
      let errorMessage;
      let errorResponse = null;
      
      // Try to parse the response body for error details
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          errorResponse = await response.json();
          // Use the error message from the response if available
          if (errorResponse.error) {
            errorMessage = errorResponse.error;
          }
        }
      } catch (parseError) {
        console.warn('Could not parse error response:', parseError);
      }
      
      // Fallback to generic messages if no specific error message
      if (!errorMessage) {
        switch (response.status) {
          case 401:
            errorMessage = "Authentication failed. Please log in again.";
            break;
          case 403:
            errorMessage = "You don't have permission to access this resource.";
            break;
          case 404:
            errorMessage = "The requested resource was not found.";
            break;
          case 500:
            errorMessage = "Internal server error. Please try again later.";
            break;
          case 503:
            errorMessage = "The service is temporarily unavailable. Please try again in a few moments.";
            break;
          default:
            errorMessage = `An error occurred while processing your request (Status: ${response.status})`;
        }
      }
      
      // Create an error object that includes the parsed response data
      const error = new Error(errorMessage);
      error.response = errorResponse;
      error.status = response.status;
      throw error;
    }
    
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    
    return await response.text();
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    
    if (error.message) {
      throw error;
    }
    
    throw new Error('An unexpected error occurred while processing your request.');
  }
};

export default API_CONFIG;
